import React, { useState } from "react";
import axios from "axios";
import { ethers } from "ethers";
import { Dialog, DialogTitle, DialogContent, TextField, Button, CircularProgress, IconButton, Checkbox, FormControlLabel, Select, MenuItem, Typography } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { SERVER_NAME } from './config';

import E3DTokenABI from "./E3DToken_ABI.json";
import NFTManagerABI from "./E3DNFTManager_ABI.json";

const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";
const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
const MINT_FEE = ethers.utils.parseUnits("100", 18); // 100 E3D

const MintNFTDialog = ({ open, onClose, setShowMintNFTDialog, userAddress, graphRef, nodes }) => {
  const [nftName, setNftName] = useState("");
  const [nftDescription, setNftDescription] = useState("");
  const [listForSale, setListForSale] = useState(false);
  const [saleAmount, setSaleAmount] = useState("");
  const [saleCurrency, setSaleCurrency] = useState("E3D");
  const [isMinting, setIsMinting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const mintNFT = async (metadataURI, linkedNFTs) => {
    try {
      if (!window.ethereum) throw new Error("MetaMask is not installed.");
      await window.ethereum.request({ method: "eth_requestAccounts" });

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const e3dToken = new ethers.Contract(E3DToken_ADDRESS, E3DTokenABI, signer);
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);

      const approvalTx = await e3dToken.approve(NFTManager_ADDRESS, MINT_FEE);
      await approvalTx.wait();

      const mintTx = await nftContract.mintNFT(metadataURI, linkedNFTs);
      const receipt = await mintTx.wait();

      const tokenId = receipt.events?.find(e => e.event === "Transfer")?.args?.tokenId.toString();

      if (listForSale && tokenId) {
        const price = ethers.utils.parseUnits(saleAmount, 18);
        const paymentCurrency = saleCurrency === "E3D" ? E3DToken_ADDRESS : ethers.constants.AddressZero;

        const listTx = await nftContract.listNFT(tokenId, price, paymentCurrency);
        await listTx.wait();
      }

      setIsMinting(false);
      return { success: true };
    } catch (error) {
      console.error("❌ mintNFT error:", error);
      return { success: false, error: error.message };
    }
  };

  const captureGraphAsPNG = async () => {
    if (!graphRef?.current) return null;

    const renderer = graphRef.current.renderer();
    const scene = graphRef.current.scene();
    const camera = graphRef.current.camera();

    renderer.render(scene, camera);

    const gl = renderer.getContext();
    const width = gl.drawingBufferWidth;
    const height = gl.drawingBufferHeight;

    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");

    const imageData = new ImageData(new Uint8ClampedArray(pixels), width, height);
    context.putImageData(imageData, 0, 0);
    context.translate(0, canvas.height);
    context.scale(1, -1);
    context.drawImage(canvas, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          resolve(null);
        }
      }, "image/png");
    });
  };

  const uploadToIPFS = async (graphImageBlob, name, description) => {
    try {
      const formData = new FormData();
      formData.append("file", graphImageBlob, "graph.png");
      formData.append("name", name);
      formData.append("description", description);

      const response = await axios.post(`${SERVER_NAME}/uploadToIPFS`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        return response.data.metadata;
      } else {
        throw new Error(response.data.error || "Upload failed");
      }
    } catch (error) {
      console.error("❌ uploadToIPFS error:", error);
      return null;
    }
  };

  const handleMint = async () => {
    if (!userAddress) {
      setStatusMessage("Please connect your wallet first.");
      return;
    }

    if (!nftName.trim()) {
      setStatusMessage("NFT Name is required.");
      return;
    }

    if (listForSale) {
      if (!saleAmount || isNaN(saleAmount) || Number(saleAmount) <= 0) {
        setStatusMessage("Sale amount must be a positive number.");
        return;
      }
    }

    setIsMinting(true);
    setStatusMessage("");

    try {
      const graphImageBlob = await captureGraphAsPNG();
      if (!graphImageBlob) throw new Error("Failed to capture graph");

      const metadataURI = await uploadToIPFS(graphImageBlob, nftName, nftDescription);
      if (!metadataURI) throw new Error("Failed to upload metadata");

      const nftAddresses = nodes
        .filter((node) => node.id.includes("."))
        .map((node) => node.id.split(".")[0].toLowerCase())
        .filter((address, index, self) => self.indexOf(address) === index);

      const result = await mintNFT(metadataURI, nftAddresses);

      if (result.success) {
        setStatusMessage("NFT Minted Successfully!");
        setTimeout(() => {
          setShowMintNFTDialog(false);
        }, 1500);
      } else {
        setStatusMessage("Minting failed: " + result.error);
      }
    } catch (error) {
      console.error("❌ handleMint error:", error);
      setStatusMessage("Minting failed.");
    }

    setIsMinting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Mint E3D NFT
        <IconButton style={{ position: "absolute", right: 10, top: 10 }} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField fullWidth label="NFT Name" value={nftName} onChange={(e) => setNftName(e.target.value)} margin="dense" />
        <TextField fullWidth label="NFT Description" multiline rows={3} value={nftDescription} onChange={(e) => setNftDescription(e.target.value)} margin="dense" />

        <FormControlLabel
          control={<Checkbox checked={listForSale} onChange={(e) => setListForSale(e.target.checked)} />}
          label="List NFT for Sale After Minting"
        />

        {listForSale && (
          <>
            <TextField
              fullWidth
              label="Sale Amount"
              value={saleAmount}
              onChange={(e) => setSaleAmount(e.target.value)}
              margin="dense"
            />
            <Select
              fullWidth
              value={saleCurrency}
              onChange={(e) => setSaleCurrency(e.target.value)}
              margin="dense"
            >
              <MenuItem value="E3D">E3D Token</MenuItem>
              <MenuItem value="ETH">ETH</MenuItem>
            </Select>
          </>
        )}

        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleMint}
          disabled={isMinting}
          style={{ marginTop: 20 }}
        >
          {isMinting ? <CircularProgress size={24} /> : "Mint NFT"}
        </Button>

        {statusMessage && (
          <Typography variant="body2" style={{ marginTop: 15, color: statusMessage.includes("failed") ? "red" : "#4d79ff" }}>
            {statusMessage}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MintNFTDialog;
